import { useMemo, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import { UserManagementPanel, ScopedSelectionEditor, ThemedText, ThemedBadge, ThemedButton, ConfirmModal } from '@satsankalpa-foundation-inc/ui';
import type { UserManagementAdapter, UserManagementPayload, UserManagementUser } from '@satsankalpa-foundation-inc/ui';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../utils/api';
import { assignmentRows, assignmentOptions, assignmentScopes, sameAssignments } from '../utils/accountScopes';
import type { ScopeUnit as Unit, ScopeProgram as Program, AccountGrant as Grant, ScopeRow } from '../utils/accountScopes';

interface ResendAllResult {total:number;sent:number;failed:string[]}
const resendAllMessage=({total,sent,failed}:ResendAllResult)=>total===0?'No pending accounts need an invitation.'
  :`Sent ${sent} of ${total} invitation${total===1?'':'s'}.${failed.length?` Delivery failed for ${failed.join(', ')}.`:''}`;

/** Bulk counterpart to the panel's per-account Resend Invitation action. */
function ResendAllInvitations() {
  const [confirming,setConfirming]=useState(false),[sending,setSending]=useState(false);
  const [status,setStatus]=useState<{failed:boolean;text:string}|null>(null);
  const resend=async()=>{
    setConfirming(false);setSending(true);setStatus(null);
    try {
      const result:ResendAllResult=await apiFetch('/api/v1/accounts/reinvite-pending',{method:'POST',body:'{}'});
      setStatus({failed:result.failed.length>0,text:resendAllMessage(result)});
    } catch(error) {
      setStatus({failed:true,text:error instanceof Error?error.message:'Invitations could not be sent'});
    } finally {setSending(false);}
  };
  return <div className="flex flex-wrap items-center justify-end gap-3">
    {status && <span role="status">{status.failed
      ?<ThemedBadge variant="error">{status.text}</ThemedBadge>
      :<ThemedText variant="muted" className="text-sm">{status.text}</ThemedText>}</span>}
    <ThemedButton variant="outline" size="sm" icon={Send} loading={sending} disabled={sending}
      onClick={()=>setConfirming(true)}>Resend All Invitations</ThemedButton>
    <ConfirmModal isOpen={confirming} onClose={()=>setConfirming(false)} onConfirm={resend} variant="warning"
      title="Resend all invitations" confirmLabel="Resend All"
      message="Email a new invitation to every active account that has not finished signup?" />
  </div>;
}

const roles = {admin:{label:'Admin',optionLabel:'Admin - Full access',variant:'error'},editor:{label:'Write',optionLabel:'Write - Can edit assigned teams',variant:'success'},reader:{label:'Read Only',optionLabel:'Read Only - View only',variant:'neutral'}} as const;

/** Operations data/policy adapter for Shared user management and assignment controls. */
export default function AccountsPage() {
  const {user}=useAuth();
  const [regions,setRegions]=useState<string[]>([]);
  const [programs,setPrograms]=useState<Program[]>([]),[units,setUnits]=useState<Unit[]>([]);
  const [existingRows,setExistingRows]=useState<ScopeRow[]>([]);
  const usersRef=useRef<UserManagementUser[]>([]);
  const adapter=useMemo<UserManagementAdapter>(()=>{
    const input=(form:UserManagementPayload,old?:UserManagementUser)=>{
      const payload:Record<string,unknown>={email:form.email,name:form.name,role:form.role,
        active:old?.active ?? true,approved:form.metadata.approved===true};
      const rows=(form.metadata.scopeRows || []) as ScopeRow[];
      const previous=(old?.metadata?.scopeRows || []) as ScopeRow[];
      if(form.role!=='admin' && (!old || old.role==='admin' || !sameAssignments(rows,previous))){
        payload.scopes=assignmentScopes(rows);
      }
      return payload;
    };
    return {
      list:async()=>{
        const [accounts,access]=await Promise.all([apiFetch('/api/v1/accounts'),apiFetch('/api/v1/access')]);
        setUnits(access.units);setPrograms(access.programs);
        setRegions(access.regions);
        const mapped=accounts.users.map((account:UserManagementUser & {approved:boolean})=>{
          const grants:Grant[]=access.grants.filter((grant:Grant)=>grant.subjectId===account.id);
          const names=[...new Set(grants.map(grant=>grant.unitId==='*'?'All Teams':access.programs.find((program:Program)=>program.unitIds.includes(grant.unitId))?.name || access.units.find((unit:Unit)=>unit.id===grant.unitId)?.name || grant.unitId))] as string[];
          return {...account,team:account.role==='admin'?'All Teams':names.join(', ') || 'Unassigned',
            teams:account.role==='admin'?['All Teams']:names.length?names:['Unassigned'],
            metadata:{approved:account.approved,scopeRows:account.role==='admin'?[]:assignmentRows(grants,access.programs)}};
        });
        setExistingRows(mapped.flatMap((account:UserManagementUser)=>(account.metadata?.scopeRows || []) as ScopeRow[]));
        usersRef.current=mapped;return mapped;
      },
      create:async form=>{const result=await apiFetch('/api/v1/accounts',{method:'POST',body:JSON.stringify(input(form))});
        if(result.invitation?.sent===false) return {message:'User created, but invitation delivery failed. Use Resend Invitation to retry.'};},
      update:async(id,form)=>{const old=usersRef.current.find(user=>user.id===id);
        if(!old)throw new Error('User list changed. Refresh and try again.');
        const result=await apiFetch(`/api/v1/accounts/${encodeURIComponent(id)}`,{method:'PUT',body:JSON.stringify(input(form,old))});
        if(result.invitation?.sent===false)return {message:'User updated, but verification email delivery failed. Use Resend Invitation to retry.'};},
      remove:async account=>{await apiFetch(`/api/v1/accounts/${encodeURIComponent(account.id)}`,{method:'DELETE'});},
      toggleActive:async account=>{await apiFetch(`/api/v1/accounts/${encodeURIComponent(account.id)}`,{method:'PATCH',body:JSON.stringify({active:!account.active})});},
      reinvite:async account=>{await apiFetch(`/api/v1/accounts/${encodeURIComponent(account.id)}/reinvite`,{method:'POST',body:'{}'});},
    };
  },[]);
  if(user?.role!=='admin')return <p role="alert">Administrator access is required.</p>;
  const defaultRows:ScopeRow[]=[{dimension:'*',values:programs[0]?.unitIds.length?[`p:${programs[0].id}`]:[]}];
  return <div className="space-y-3"><UserManagementPanel adapter={adapter} headerActions={<ResendAllInvitations />} teams={['All Teams',...programs.map(program=>program.name)]}
    roles={roles} defaultRole="reader" defaultTeam={programs[0]?.name || 'All Teams'}
    defaultMetadata={{approved:true,scopeRows:defaultRows}} currentUserId={user.id} features={[]}
    isPendingUser={account=>account.signupStatus==='pending' || account.metadata?.approved===false}
    renderAccountStatus={account=>account.metadata?.approved===false?<ThemedBadge variant="warning">Approval Pending</ThemedBadge>:null}
    renderTeamField={(values,setValues)=>values.role==='admin'
      ?<ThemedText variant="muted" className="text-sm">Administrators have access to all programs, streams and regions.</ThemedText>
      :<ScopedSelectionEditor label="Program / Stream Access" selectionLabel="Programs / Streams" dimensionLabel="Region"
        options={assignmentOptions(programs,units,existingRows)} exclusiveValue="*"
        dimensions={[{id:'*',label:'All Regions'},...[...new Set([...regions,...existingRows.map(row=>row.dimension)])].filter(region=>region!=='*').map(region=>({id:region,label:region}))]}
        value={(values.metadata.scopeRows || []) as ScopeRow[]}
        onChange={scopeRows=>setValues(old=>({...old,metadata:{...old.metadata,scopeRows}}))}
        addLabel="Add Region Assignment" emptyMessage="No programs selected grants no activity access for this assignment." />}
    renderFields={(values,setValues,account)=><>
      <label className="flex gap-2 items-center text-sm text-slate-700 dark:text-slate-300"><input type="checkbox" aria-label="Approve Access"
        checked={values.metadata.approved===true} disabled={account?.id===user.id}
        onChange={event=>setValues(old=>({...old,metadata:{...old.metadata,approved:event.target.checked}}))}
        className="rounded border-slate-300 text-purple-600 focus:ring-purple-500 dark:border-slate-600 dark:bg-slate-700" />Approve Access</label>
    </>} /></div>;
}
